import { useState, useCallback, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Info, Square, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ScheduledRun } from '@/services/scheduled-runs-api';
import type { VMOSDevice } from '@/services/vmos-api';
import { DeviceCard } from './DeviceCard';
import { DeviceDetailSheet } from './DeviceDetailSheet';
import type { DeviceStreamState } from './shared-types';
import { subscribeToGroupStream, getCachedDeviceStates } from '@/services/group-stream-store';

export const ActiveRunPanel = ({
  run,
  deviceMap,
  onStop,
  onViewDetails,
}: {
  run: ScheduledRun;
  deviceMap: Record<string, VMOSDevice>;
  onStop: () => void;
  onViewDetails: () => void;
}) => {
  const padIds = useMemo(
    () => (run.config?.pad_ids || []) as string[],
    [run.config?.pad_ids],
  );

  const [deviceStates, setDeviceStates] = useState<Record<string, DeviceStreamState>>(() => {
    if (run.last_group_id) {
      const cached = getCachedDeviceStates(run.last_group_id);
      if (cached) return cached;
    }
    return {};
  });

  const hasState = Object.keys(deviceStates).length > 0;

  const [selectedPadId, setSelectedPadId] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamEnded, setStreamEnded] = useState(false);

  useEffect(() => {
    if (!run.last_group_id || streamEnded) return;

    setIsStreaming(true);

    const unsubscribe = subscribeToGroupStream(
      run.last_group_id,
      padIds,
      (padId, stateUpdate) => {
        setDeviceStates(prev => {
          const current = prev[padId] || { status: 'running' as const, step: 0, maxSteps: 200, messages: [] };
          return {
            ...prev,
            [padId]: {
              ...current,
              ...stateUpdate,
              messages: stateUpdate.messages
                ? stateUpdate.messages
                : current.messages || [],
            },
          };
        });
      },
      () => {
        setIsStreaming(false);
        setStreamEnded(true);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [run.last_group_id, padIds, streamEnded]);

  const handleStop = useCallback(() => {
    setIsStreaming(false);
    setStreamEnded(true);
    onStop();
  }, [onStop]);

  const completedCount = padIds.filter(pid => {
    const s = deviceStates[pid]?.status;
    return s === 'completed' || s === 'failed' || s === 'stopped';
  }).length;

  const isInitializing = !run.last_group_id;
  const isLoadingState = !isInitializing && !hasState;

  return (
    <div className="bg-card">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-3 border-b">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="relative flex h-3 w-3 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500" />
          </span>
          <h3 className="font-semibold text-sm truncate">
            {run.name} — running
          </h3>
          <Button
            variant="ghost"
            size="icon"
            title="View schedule details"
            onClick={onViewDetails}
          >
            <Info className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground font-mono tabular-nums">
            {padIds.length} {padIds.length === 1 ? 'device' : 'devices'}
          </span>
          {hasState && (
            <span className="text-xs text-muted-foreground font-mono tabular-nums">
              {completedCount}/{padIds.length} done
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isStreaming && (
            <span className="inline-flex items-center gap-1.5 text-xs text-green-600">
              <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
              streaming
            </span>
          )}
          <Button variant="outline" size="sm" onClick={handleStop}>
            <Square className="mr-2 h-3 w-3" />
            Stop All
          </Button>
        </div>
      </div>

      {/* Device Cards */}
      {isInitializing ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm p-6">
          <Loader2 className="h-4 w-4 animate-spin" />
          Initializing run...
        </div>
      ) : isLoadingState ? (
        <div className="p-4">
          <div className={cn(
            'grid gap-4 auto-rows-[350px]',
            padIds.length <= 2 ? 'grid-cols-1 md:grid-cols-2'
            : padIds.length <= 4 ? 'grid-cols-1 md:grid-cols-2'
            : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
          )}>
            {padIds.map((padId) => (
              <div
                key={padId}
                className="rounded-lg border bg-muted/30 flex flex-col items-center justify-center gap-2 text-muted-foreground"
              >
                <Loader2 className="h-6 w-6 animate-spin" />
                <span className="text-sm font-medium">
                  {deviceMap[padId]?.device_name || padId.slice(0, 8)}
                </span>
                <span className="text-xs">Loading state...</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="p-4">
          <div
            className={cn(
              'grid gap-4 auto-rows-[350px]',
              padIds.length <= 2
                ? 'grid-cols-1 md:grid-cols-2'
                : padIds.length <= 4
                  ? 'grid-cols-1 md:grid-cols-2'
                  : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
            )}
          >
            {padIds.map((padId) => (
              <DeviceCard
                key={padId}
                name={deviceMap[padId]?.device_name || padId.slice(0, 8)}
                state={
                  deviceStates[padId] || {
                    status: 'running' as const,
                    step: 0,
                    maxSteps: 200,
                    messages: [],
                  }
                }
                isSelected={selectedPadId === padId}
                onClick={() =>
                  setSelectedPadId((prev) => (prev === padId ? null : padId))
                }
              />
            ))}
          </div>
        </div>
      )}

      {/* Device Detail Sheet */}
      {selectedPadId && deviceStates[selectedPadId] && (
        <DeviceDetailSheet
          padId={selectedPadId}
          deviceName={
            deviceMap[selectedPadId]?.device_name ||
            selectedPadId.slice(0, 8)
          }
          state={deviceStates[selectedPadId]}
          open={!!selectedPadId}
          onOpenChange={(open) => {
            if (!open) setSelectedPadId(null);
          }}
        />
      )}
    </div>
  );
};
